import React, { useContext, useEffect, useState } from "react";
import {
  Tooltip,
  Input,
  Select,
  Dropdown,
  Menu,
  Divider,
  Message,
  Button,
  Drawer,
  Form,
  Space,
} from "@arco-design/web-react";
import {
  IconLanguage,
  IconNotification,
  IconPoweroff,
  IconLoading,
  IconSafe,
  IconLock,
} from "@arco-design/web-react/icon";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { GlobalState } from "@/store";
import { GlobalContext } from "@/context";
import useLocale from "@/utils/useLocale";
import Logo from "@/assets/logo.png";
import MessageBox from "@/components/MessageBox";
import IconButton from "./IconButton";
import styles from "./style/index.module.less";
import defaultLocale from "@/locale";
import useStorage from "@/utils/useStorage";
import { clearAuthToken } from "@/utils/token";
import request from "@/utils/request";
import CircleAvatar from "@/components/CircleAvatar";
import AnnouncementBar from "@/components/AnnouncementBar";
import { updateAdminPassword } from "@/services/user/profile";

function Navbar({ show }: { show: boolean }) {
  const t = useLocale();
  const { userInfo, userLoading } = useSelector((state: GlobalState) => state);
  const dispatch = useDispatch();
  const history = useHistory();

  const [, setUserStatus] = useStorage("userStatus");
  const [role] = useStorage("userRole", "admin");
  const [passwordDrawerVisible, setPasswordDrawerVisible] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [passwordForm] = Form.useForm();

  const { setLang, lang } = useContext(GlobalContext);

  async function logout() {
    try {
      await request.get("/admin/logout");
    } catch (error) {
      // 忽略登出接口异常，仍继续清理本地状态
    }
    clearAuthToken();
    setUserStatus("logout");
    window.location.href = "/login";
  }

  const openPasswordDrawer = () => {
    passwordForm.resetFields();
    setPasswordDrawerVisible(true);
  };

  const handlePasswordSubmit = async () => {
    try {
      const values = await passwordForm.validate();
      if (values.newPassword !== values.confirmPassword) {
        Message.error("两次输入的密码不一致");
        return;
      }
      const username =
        userInfo?.username || userInfo?.accountId || userInfo?.name;
      if (!username) {
        Message.error("无法获取当前账号信息，请重新登录后再试");
        return;
      }
      setPasswordSubmitting(true);
      const response = await updateAdminPassword({
        username,
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
      });
      if (!response || response.code !== 200) {
        throw new Error(response?.message || "修改密码失败");
      }
      Message.success("密码修改成功");
      setPasswordDrawerVisible(false);
      passwordForm.resetFields();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      Message.error(
        error?.message || error?.response?.data?.message || "修改密码失败"
      );
    } finally {
      setPasswordSubmitting(false);
    }
  };

  function onMenuItemClick(key: string) {
    switch (key) {
      case "balance":
        history.push("/user/profile");
        break;
      case "password":
        openPasswordDrawer();
        break;
      case "logout":
        logout();
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    if (process.env.REACT_APP_USE_MOCK !== "true") {
      return;
    }
    const authorities =
      role === "admin"
        ? [
            "sys:user:list",
            "sys:user:add",
            "sys:user:edit",
            "sys:user:delete",
            "sys:user:import",
            "sys:user:export",
            "sys:cdk:generate",
            "sys:cdk:unlink",
            "sys:cdk:delete",
            "sys:cdk:export",
          ]
        : ["sys:user:list"];
    dispatch({
      type: "update-userInfo",
      payload: {
        userInfo: {
          ...userInfo,
          authorities,
        },
      },
    });
  }, [role, dispatch, userInfo]);

  if (!show) {
    return null;
  }

  const droplist = (
    <Menu onClickMenuItem={onMenuItemClick}>
      <Menu.Item key="balance">
        <IconSafe className={styles["dropdown-icon"]} />
        {t["menu.user.balance"] || "用户余额"}
      </Menu.Item>
      <Menu.Item key="password">
        <IconLock className={styles["dropdown-icon"]} />
        {t["navbar.changePassword"] || "修改密码"}
      </Menu.Item>
      <Divider style={{ margin: "4px 0" }} />
      <Menu.Item key="logout">
        <IconPoweroff className={styles["dropdown-icon"]} />
        {t["navbar.logout"]}
      </Menu.Item>
    </Menu>
  );

  return (
    <div className={styles.navbar}>
      <div className={styles.left}>
        <div className={styles.logo}>
          <img src={Logo} alt="logo" width={20} height={20} />
          <div className={styles["logo-name"]}>后台管理系统</div>
        </div>
      </div>
      <AnnouncementBar />
      <ul className={styles.right}>
        <li>
          <Select
            triggerElement={<IconButton icon={<IconLanguage />} />}
            options={[
              { label: "中文", value: "zh-CN" },
              { label: "English", value: "en-US" },
            ]}
            value={lang}
            triggerProps={{
              autoAlignPopupWidth: false,
              autoAlignPopupMinWidth: true,
              position: "br",
            }}
            trigger="hover"
            onChange={(value) => {
              setLang(value);
              const nextLang = defaultLocale[value];
              Message.info(`${nextLang["message.lang.tips"]}${value}`);
            }}
          />
        </li>
        <li>
          <MessageBox>
            <IconButton icon={<IconNotification />} />
          </MessageBox>
        </li>
        {userInfo && (
          <li>
            <Dropdown droplist={droplist} position="br" disabled={userLoading}>
              <CircleAvatar
                size={32}
                text={
                  userInfo.name ||
                  userInfo.realname ||
                  userInfo.username ||
                  userInfo.accountId ||
                  "U"
                }
                className={styles["navbar-avatar"]}
              >
                {userLoading ? <IconLoading /> : undefined}
              </CircleAvatar>
            </Dropdown>
          </li>
        )}
      </ul>
      <Drawer
        title="修改密码"
        width={360}
        visible={passwordDrawerVisible}
        onCancel={() => {
          setPasswordDrawerVisible(false);
          passwordForm.resetFields();
        }}
        footer={null}
        unmountOnExit
        mountOnEnter
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onSubmit={handlePasswordSubmit}
        >
          <Form.Item
            label="旧密码"
            field="oldPassword"
            rules={[{ required: true, message: "请输入旧密码" }]}
          >
            <Input.Password placeholder="请输入旧密码" />
          </Form.Item>
          <Form.Item
            label="新密码"
            field="newPassword"
            rules={[
              { required: true, message: "请输入新密码" },
              { minLength: 6, message: "密码至少 6 位字符" },
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item
            label="确认密码"
            field="confirmPassword"
            rules={[{ required: true, message: "请再次输入新密码" }]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
          <Space style={{ marginTop: 12 }}>
            <Button onClick={() => setPasswordDrawerVisible(false)}>
              取消
            </Button>
            <Button
              type="primary"
              loading={passwordSubmitting}
              onClick={handlePasswordSubmit}
            >
              确认修改
            </Button>
          </Space>
        </Form>
      </Drawer>
    </div>
  );
}

export default Navbar;
